import { useContext, useEffect, useState } from "react";
import Logo from "../../../shared/components/logo";
import {
  Box,
  Button,
  CssBaseline,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import { styled, CSSObject, Theme } from "@mui/material/styles";
import SidebarMenu from "./SidebarMenu";
import { SidebarContext } from "../../../contexts/SidebarContext";
import useLazyQuery from "../../../shared/components/hooks/useLazyQuery";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import useMutation from "../../../shared/components/hooks/useMutation";
import {
  getLocalStorageData,
  setLocalStorageData,
} from "../../../shared/utils/localStorage";
import { UserType } from "../../../shared/enum";
import "./styles.css";

const SidebarWrapper = styled(Box)(
  ({ theme }) => `
    color: ${theme.sidebar.textColor};
    background: ${theme.sidebar.background};
    box-shadow: ${theme.sidebar.boxShadow};
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
`
);

const TopSection = styled(Box)(
  ({ theme }) => `
    display: flex;
    height: 87px;
    align-items: center;
    justify-content: space-between;
    // padding: 0 15px;
    background: ${theme.sidebar.background};
    &.manual-expand {
      width: 100%;
      justify-content: space-between;
    }
    &.sidebar-toggle{
      width: 100%;
      justify-content: space-between;
    }
`
);

const drawerWidth = 280;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.standard,
  }),
  overflow: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.standard,
  }),
  overflow: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  backgroundColor: theme.sidebar.background,
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

function Sidebar() {
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const { data: userData, fetchData } = useLazyQuery("admin/roles/permissions");
  const [manualExpand, setManualExpand] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);


  const { loading: apiLoading, modifyData } = useMutation(
    "/organization-management/organizations"
  );
  const userDataLocalStorage = getLocalStorageData("userData") || {};

  const handleAdminLogoutFromOrg = async () => {
    const response = await modifyData(
      `/admin/organization/superAdmin-organization-logout`,
      "post"
    );
    if (response?.status) {
      if (userDataLocalStorage) {
        userDataLocalStorage.userType = UserType.SuperAdmin;
        userDataLocalStorage.orgID = null;
        setLocalStorageData("superAdminOrgLogin", JSON.stringify(false));
        setLocalStorageData("userData", userDataLocalStorage);
        window.dispatchEvent(new Event("userDataUpdated"));
        window.location.href = "/organization-management/organizations";
      } else {
        console.error(
          "No userDataLocalStorage found in localStorage for the specified key."
        );
      }
    }
  };

  const handleToggle = () => {
    setManualExpand((prev) => !prev);
  };


  return (
    <Box
      display="flex"
      onMouseOver={() => !manualExpand && toggleSidebar(true)}
      onMouseOut={() => !manualExpand && toggleSidebar(false)}
    >
      <CssBaseline />
      <Drawer variant="permanent" open={sidebarToggle || manualExpand}>
        <DrawerHeader>
          <TopSection
            className={manualExpand ? "manual-expand" : "sidebar-toggle"}
          >
            <Logo
              variant={sidebarToggle || manualExpand ? "primary" : "secondary"}
              color="white"
            />
            {(sidebarToggle || manualExpand) && (
              <Tooltip title={manualExpand ? "Collapse" : "Expand"}>
                <IconButton
                  onClick={handleToggle}
                  color="inherit"
                  className={
                    manualExpand
                      ? "manual-expand-button"
                      : "sidebar-toggle-button"
                  }
                >
                  <MenuOpenIcon />
                </IconButton>
              </Tooltip>
            )}
          </TopSection>
        </DrawerHeader>
        <SidebarWrapper>
          <SidebarMenu userData={(userData as any)?.data} />
        </SidebarWrapper>

        {JSON.parse(getLocalStorageData("superAdminOrgLogin") || "false") ? (
          <IconButton
            className="goBackButton"
            size="medium"
            onClick={handleAdminLogoutFromOrg}
          >
            <ArrowBackIosNewIcon />
            {(sidebarToggle || manualExpand) && <span>Exit to App</span>}
          </IconButton>
        ) : null}
      </Drawer>
    </Box>
  );
}

export default Sidebar;
